import React from 'react';

import {Box, FormControl, Grid, Stack, Typography, Tooltip} from '@mui/material';
import {HelpOutline as HelpOutlineIcon} from '@mui/icons-material';
import {useTheme} from '@mui/material/styles';
import {useForm} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import API, {
  BlobData,
  Types,
  VehicleGetProps,
  VehicleUpdateRequest,
  useAuth,
  useCsrfToken,
} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {CircleLoader, EditableImageField, LoadError, ValidatableField, useErrorHandler} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  errorNoVehicle: {
    id: 'form.VehicleEditForm.error.noVehicle',
    defaultMessage: 'vehicle data not found',
  },
  buttonCancel: {
    id: 'form.VehicleEditForm.button.cancel',
    defaultMessage: 'Cancel',
  },
  buttonSubmit: {
    id: 'form.VehicleEditForm.button.submit',
    defaultMessage: 'Submit',
  },
  inputImageHelperText1: {
    id: 'form.VehicleEditForm.input.imageHelperText1',
    defaultMessage: 'The file size must be less than is 10 MB.',
  },
  inputImageHelperText2: {
    id: 'form.VehicleEditForm.input.imageHelperText2',
    defaultMessage: 'This image will be displayed in a square with an aspect ratio of 1:1.',
  },
  inputNameLabel: {
    id: 'form.VehicleEditForm.input.nameLabel',
    defaultMessage: 'Name',
  },
  inputNamePlaceholder: {
    id: 'form.VehicleEditForm.input.namePlaceholder',
    defaultMessage: 'Vehicle name',
  },
  inputSerialNumberLabel: {
    id: 'form.VehicleEditForm.input.serialNumberLabel',
    defaultMessage: 'Serial Number',
  },
  inputSerialNumberPlaceholder: {
    id: 'form.VehicleEditForm.input.serialNumberlaceholder',
    defaultMessage: 'Serial number of your vehicle',
  },
  successVehicleCreated: {
    id: 'form.VehicleEditForm.success.vehicleCreated',
    defaultMessage: 'Vehicle created',
  },
  successVehicleUpdated: {
    id: 'form.VehicleEditForm.success.accountUpdated',
    defaultMessage: 'Vehicle updated',
  },
  successImageUpdated: {
    id: 'form.VehicleEditForm.success.imageUpdated',
    defaultMessage: 'Image updated',
  },
  successImageDeleted: {
    id: 'form.VehicleEditForm.success.imageDeleted',
    defaultMessage: 'Image deleted',
  },
});

interface Props {
  vehicleId?: string;
  locked?: boolean;
}

function VehicleEditForm({vehicleId, locked = false}: Props): JSX.Element {
  const intl = useIntl();
  const theme = useTheme();
  const {account} = useAuth();
  const {csrfToken} = useCsrfToken();
  const navigate = useNavigate();
  const {register, formState} = useForm({mode: 'onChange'});
  const {errors} = formState;
  const {showAndEmitJsErrorEvent, showError} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const [editable, setEditable] = React.useState(false);
  const [name, setName] = React.useState<string>();
  const [serialNumber, setSerialNumber] = React.useState<string>();
  const options: VehicleGetProps = {preload: ['groups', 'group_membership', 'owner']};
  const {data, isError, isLoading} = useQuery(
    vehicleId ? API.Vehicles.getKey(vehicleId, options) : '',
    () => (vehicleId ? API.Vehicles.get(vehicleId, options) : undefined),
    {enabled: !!vehicleId},
  );
  const {mutate: updateVehicle} = useMutation<unknown, unknown, VehicleUpdateRequest>(
    (props: VehicleUpdateRequest) => {
      if (!data) {
        return Promise.reject(intl.formatMessage(msgs.errorNoVehicle));
      }
      return API.Vehicles.update(csrfToken, data.id, props);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(API.Vehicles.CacheKey);
        showSuccess(intl.formatMessage(msgs.successVehicleUpdated));
        navigate('.', {replace: true});
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const saveName = (newValue: string) => {
    if (!errors['name']) {
      updateVehicle({name: newValue});
      return true;
    }
    return false;
  };
  const saveSerialNumber = (newValue: string) => {
    if (!errors['serialNumber']) {
      updateVehicle({serialNumber: newValue});
      return true;
    }
    return false;
  };
  const {mutate: updateImage} = useMutation<unknown, unknown, VehicleUpdateRequest>(
    (props: VehicleUpdateRequest) => {
      if (!data) {
        return Promise.reject(intl.formatMessage(msgs.errorNoVehicle));
      }
      return API.Vehicles.update(csrfToken, data.id, props);
    },
    {
      onSuccess: (_, props: VehicleUpdateRequest) => {
        if (!data) {
          return;
        }
        queryClient.invalidateQueries(API.Vehicles.CacheKey);
        if (props.image) {
          showSuccess(intl.formatMessage(msgs.successImageUpdated));
        } else if (props.deleteImage && data.imageUrl) {
          showSuccess(intl.formatMessage(msgs.successImageDeleted));
        }
        navigate('.', {replace: true});
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const handleImageUpdate = React.useCallback(
    (blobData?: BlobData) => {
      if (!data) {
        showError(new Error(intl.formatMessage(msgs.errorNoVehicle)));
        return;
      }
      if (blobData) {
        updateImage({image: blobData});
      } else if (data.imageUrl) {
        updateImage({deleteImage: true});
      }
    },
    [data, updateImage, intl, showError],
  );

  React.useEffect(() => {
    if (data && account) {
      if (data.owner?.id === account.id) {
        setEditable(true);
        return;
      }
      const roles = data.groups?.map((e: Types.GroupVehicle) => e.group?.membership?.role);
      if (roles.indexOf(Types.GroupMemberOwnerRole) >= 0 || roles.indexOf(Types.GroupMemberEditorRole) >= 0) {
        setEditable(true);
      }
    }
  }, [data, account]);

  if (isLoading) {
    return <CircleLoader />;
  } else if (isError) {
    return <LoadError />;
  }
  return (
    <form>
      <Grid container spacing={1} columnSpacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl component="fieldset" fullWidth>
            <Box
              sx={{
                '.filepond--wrapper': {
                  margin: '0 auto',
                },
                '.filepond--credits': {
                  display: 'none',
                },
                '.filepond--drop-label': {
                  color: theme.palette.text.secondary,
                },
              }}>
              <EditableImageField
                {...{editable, locked}}
                url={data?.imageUrl}
                onSave={handleImageUpdate}
                maxWidth={250}
                maxHeight={250}
                maxFileSize="10MB"
                stylePanelLayout="integrated"
              />
            </Box>
            <Stack direction="row" sx={{justifyContent: 'space-between', alignItems: 'center'}}>
              <Typography variant="caption">Image</Typography>
              {editable ? (
                <Tooltip
                  title={
                    <Stack>
                      <Typography variant="caption">{intl.formatMessage(msgs.inputImageHelperText1)}</Typography>
                      <Typography variant="caption">{intl.formatMessage(msgs.inputImageHelperText2)}</Typography>
                    </Stack>
                  }
                  arrow>
                  <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
              ) : null}
            </Stack>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Stack gap={1} sx={{flex: 1}}>
            {vehicleId ? (
              <FormControl component="fieldset" fullWidth>
                <ValidatableField
                  id="vehicle_type"
                  label="Vehicle type"
                  value={data?.vehicleType || 'Drone'}
                  editable={false}
                  locked
                  {...{register, errors}}
                />
              </FormControl>
            ) : null}
            {vehicleId ? (
              <FormControl component="fieldset" fullWidth>
                <ValidatableField
                  id="id"
                  label="ID"
                  value={vehicleId}
                  editable={false}
                  locked
                  {...{register, errors}}
                />
              </FormControl>
            ) : null}
            <FormControl component="fieldset" fullWidth>
              <ValidatableField
                id="serialNumber"
                label={intl.formatMessage(msgs.inputSerialNumberLabel)}
                placeholder={intl.formatMessage(msgs.inputSerialNumberPlaceholder)}
                value={serialNumber}
                registerOptions={{
                  value: data?.serialNumber,
                  required: true,
                  maxLength: 255,
                  onChange: (e) => setSerialNumber(e.target.value),
                }}
                onSave={saveSerialNumber}
                editable={false}
                locked
                {...{register, errors}}
              />
            </FormControl>
            <FormControl component="fieldset" fullWidth>
              <ValidatableField
                id="name"
                label={intl.formatMessage(msgs.inputNameLabel)}
                placeholder={intl.formatMessage(msgs.inputNamePlaceholder)}
                value={name}
                registerOptions={{
                  value: data?.name,
                  required: true,
                  maxLength: 255,
                  onChange: (e) => setName(e.target.value),
                }}
                onSave={saveName}
                {...{register, errors, editable, locked}}
              />
            </FormControl>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
}

export default React.memo(VehicleEditForm);
